import { normalizeIsoCountryCode } from '@/lib/commerce/quote-countries';

/** The accepted header is the checkout contract, not a possibly newer draft.
 * Older admin quotes missed the header unit price. Recover only a proven
 * single-product mirror; never collapse a multi-line quote into one product.
 */
export function quotePaymentUnitPrice(quote: {
  unitPriceCents: number | null;
  subtotalCents: number | null;
  totalCents: number | null;
  shippingCents: number | null;
  vatCents: number | null;
  quantity: number;
  productId: string;
  currency: string;
  acceptedAt?: Date | null;
  versions?: Array<{
    status: string;
    approvedAt?: Date | null;
    currency: string;
    subtotalCents: number;
    shippingCents: number;
    vatCents: number;
    totalCents: number;
    lines: Array<{ productId: string | null; quantity: number }>;
  }>;
}): number | null {
  if (!quotePaymentTotals(quote)) return null;
  const version = quote.versions?.[0];
  if (version && (version.lines.length !== 1 || version.lines[0].productId !== quote.productId)) return null;
  if (quote.unitPriceCents !== null && Number.isSafeInteger(quote.unitPriceCents) && quote.unitPriceCents >= 0) return quote.unitPriceCents;
  if (!version || version.status !== 'APPROVED'
    || (quote.acceptedAt && version.approvedAt && version.approvedAt > quote.acceptedAt)
    || version.currency !== quote.currency
    || version.lines[0].quantity !== quote.quantity
    || !Number.isSafeInteger(quote.quantity) || quote.quantity <= 0
    || quote.subtotalCents === null || !Number.isSafeInteger(quote.subtotalCents) || quote.subtotalCents <= 0
    || version.subtotalCents !== quote.subtotalCents
    || version.shippingCents !== quote.shippingCents
    || version.vatCents !== quote.vatCents
    || version.totalCents !== quote.totalCents) return null;
  // The line unit price precedes quote-level discounts. Preserve the exact
  // discounted header subtotal; rounded unit price is only a presentation unit.
  return Math.round(quote.subtotalCents / quote.quantity);
}

/** Used for display snapshots and, with DB input only, actual payment totals. */
export function quotePaymentTotals(quote: {
  subtotalCents: number | null; shippingCents: number | null;
  vatCents: number | null; totalCents: number | null;
}) {
  const { subtotalCents, shippingCents, vatCents, totalCents } = quote;
  if ([subtotalCents, shippingCents, vatCents, totalCents].some(value => value === null || !Number.isSafeInteger(value) || value < 0)
    || !totalCents || subtotalCents! + shippingCents! !== totalCents || vatCents! > totalCents) return null;
  return { subtotal: subtotalCents! / 100, shipping: shippingCents! / 100, tax: vatCents! / 100, total: totalCents / 100, discount: 0 };
}

export function quoteDisplayForCountry(value: unknown, country: string) {
  if (!value || typeof value !== 'object') return null;
  const data = value as Record<string, unknown>;
  if (typeof data.countryCode !== 'string' || data.countryCode.toUpperCase() !== country.toUpperCase()) return null;
  const { subtotal, shipping, tax, total } = data;
  if (typeof subtotal !== 'number' || typeof shipping !== 'number' || typeof tax !== 'number' || typeof total !== 'number'
    || [subtotal, shipping, tax, total].some(amount => !Number.isFinite(amount) || amount < 0)
    || total <= 0 || tax > total || Math.abs(subtotal + shipping - total) > 0.011) return null;
  const vatRateApplied = typeof data.vatRateApplied === 'number' && Number.isFinite(data.vatRateApplied)
    && data.vatRateApplied >= 0 && data.vatRateApplied <= 100 ? data.vatRateApplied : undefined;
  return { subtotal, shipping, tax, total, discount: 0, promotionDiscount: 0, combinedShippingDiscount: 0,
    ...(vatRateApplied !== undefined ? { vatRateApplied } : {}),
    ...(typeof data.reverseCharge === 'boolean' ? { reverseCharge: data.reverseCharge } : {}),
  };
}

/** One accepted quote owns the entire basket, including multi-product quotes.
 * These snapshots only control display. Payment still resolves the token,
 * products, quantities and accepted totals independently from the database.
 */
export function quoteBasketDisplayForCountry(items: Array<{ quoteToken?: string; quoteDisplay?: unknown; totalPrice?: number }>, country: string) {
  const token = items[0]?.quoteToken?.trim();
  if (!token || !items.length || items.some(item => item.quoteToken?.trim() !== token)) return null;
  const display = quoteDisplayForCountry(items[0].quoteDisplay, country);
  if (!display) return null;
  const lineCount = (items[0].quoteDisplay as Record<string, unknown>).lineCount;
  if (lineCount !== undefined && (!Number.isSafeInteger(lineCount) || lineCount !== items.length)) return null;
  // Older carts have no line count. Only retain multi-line snapshots when the
  // complete line subtotal proves that no quote product has been removed.
  if (lineCount === undefined && items.length > 1 && (items.some(item => !Number.isFinite(item.totalPrice))
    || Math.abs(items.reduce((sum, item) => sum + item.totalPrice!, 0) - display.subtotal) > 0.011)) return null;
  for (const item of items) {
    const other = quoteDisplayForCountry(item.quoteDisplay, country);
    if (!other || other.subtotal !== display.subtotal || other.shipping !== display.shipping
      || other.tax !== display.tax || other.total !== display.total
      || other.vatRateApplied !== display.vatRateApplied || other.reverseCharge !== display.reverseCharge
      || (item.quoteDisplay as Record<string, unknown>).lineCount !== lineCount) return null;
  }
  return display;
}

export function quoteDisplayVatRate(display: { tax: number; total: number; vatRateApplied?: number }) {
  return display.vatRateApplied ?? (display.tax === 0 ? 0 : Math.round(display.tax / (display.total - display.tax) * 100));
}

/**
 * Het bindende bezorgland van een offertemand: het land waarvoor de offerte is
 * gemaakt. Null zonder (eenduidige) offerte. De checkout zet het land hierop
 * vast; de server weigert een ander land (QUOTE_COUNTRY_MISMATCH /
 * UNSUPPORTED_COUNTRY).
 */
export function quoteBasketCountry(items: Array<{ quoteToken?: string; quoteDisplay?: unknown }>): string | null {
  const token = items[0]?.quoteToken?.trim();
  if (!token || items.some(item => item.quoteToken?.trim() !== token)) return null;
  const display = items[0].quoteDisplay;
  if (!display || typeof display !== 'object') return null;
  return normalizeIsoCountryCode((display as Record<string, unknown>).countryCode);
}
